import {
  Product,
  SeatMapSlotResource,
  ResourceCategory,
  Scheduling,
  ScheduleTime,
  SellingMode,
  ResourceCategoryCompleteCapacityChargeTypeEnum,
  SeatMapOverview,
  SeatMapSlot,
  SeatMapSlotTypeEnum,
  Tariff,
} from '@src/core/buyerApiClient';
import type { FullProduct, TariffsPerType } from '@src/stores/product';
import { schedulingContainsMinute } from '@src/common/schedulings';
import type { SelectedTariff } from '@src/domains/tariffs';
import {
  ChargeTypeEnum,
  SeatMapOverview as SeatMapOverviewV2,
  SlotTypeEnum,
  ShapeTypeEnum,
  SeatAllocation,
  getResourceValueCents as getSeatAllocationResourceValueCents,
} from '@planne-software/seat-map';

export const UNAVAILABLE_STATUS = 'unavailable';
export const AVAILABLE_STATUS = 'available';
export const SELECTED_STATUS = 'selected';

export const seatStatus = [AVAILABLE_STATUS, SELECTED_STATUS, UNAVAILABLE_STATUS] as const;

export const statusColors: { [key: string]: string } = {
  [AVAILABLE_STATUS]: '#34a952',
  [SELECTED_STATUS]: '#fbbb04',
  [UNAVAILABLE_STATUS]: '#aaaaaa',
};

export const isValidStatus = (stat: string) => [AVAILABLE_STATUS, SELECTED_STATUS].includes(stat);

const isDateAndTime = (prod: Product) => prod.sellingMode === SellingMode.WithDateAndTime;
const isDateOnly = (prod: Product) => prod.sellingMode === SellingMode.WithDateOnly;

export const getSchedulingFrom = (
  product: Product,
  tariffsPerType: TariffsPerType[],
  minutes?: number | null,
): Scheduling | undefined => {
  for (const tariffPerType of tariffsPerType) {
    if (tariffPerType.selectedQuantity === 0) {
      continue;
    }

    for (const scheduling of tariffPerType.schedulings) {
      if (
        isDateOnly(product) ||
        (isDateAndTime(product) && minutes != null && schedulingContainsMinute(scheduling, minutes))
      ) {
        return scheduling;
      }
    }
  }

  return undefined;
};

export const getResourceValueCents = (resource: SeatMapSlotResource, category: ResourceCategory) => {
  switch (category.completeCapacityChargeType) {
    case ResourceCategoryCompleteCapacityChargeTypeEnum.FixedValue:
      return resource.capacity * category.minCapacityUnitCents!;

    case ResourceCategoryCompleteCapacityChargeTypeEnum.TariffPercent:
      return Math.ceil(category.tariff!.priceCents! * (category.tariffPercentage! / 100)) * resource.capacity;

    default:
      return 0;
  }
};

export const getAllocationsExtrasCents = (allocations: SeatAllocation[]): number =>
  allocations.reduce((total, { seat }) => {
    if (seat.resource) {
      return total + getSeatAllocationResourceValueCents(seat.resource);
    }
    return total;
  }, 0);

export const getResourceAnalyticsData = (product: FullProduct, resource: SeatMapSlotResource) => ({
  productId: product?.id ?? '',
  productName: product?.name ?? '',
  resourceName: resource.name,
  resourceId: resource.id,
});

export const getDefaultAvailableArea = (
  areas: SeatMapOverview['areas'],
  resourceStatus: (slot: SeatMapSlotResource) => string,
) => {
  if (areas.length > 1) {
    for (const area of areas) {
      if (area.slots.some((slot) => resourceStatus(slot.resource!) === AVAILABLE_STATUS)) {
        return area;
      }
    }
  }

  return areas.length ? areas[0] : null;
};

export const getResourceMinCapacity = (category: ResourceCategory, resource: SeatMapSlotResource): number => {
  return category.allowUnderAllocation
    ? Math.ceil((category.minCapacityPercent! / 100) * resource.capacity)
    : resource.capacity;
};

interface ResourceTariffValues {
  [key: string]: number;
}

export const makeResourceTariffValues = (
  selectedTariffs: SelectedTariff[],
  resources: SeatMapSlotResource[],
  categoryIndex: { [key: string]: ResourceCategory },
): ResourceTariffValues => {
  const newTariffValues: ResourceTariffValues = {};
  const tariffPending = selectedTariffs.reduce(
    (carry: { [key: string]: number }, selTariff) => ({
      ...carry,
      [`${selTariff.tariff.id}`]: selTariff.quantity,
    }),
    {},
  );

  resources.sort((a, b) => {
    const catA = categoryIndex[a.categoryId]!;
    const catB = categoryIndex[b.categoryId]!;

    if (
      catA.allowUnderAllocation &&
      catB.allowUnderAllocation &&
      (catA.minCapacityPercent || catB.minCapacityPercent)
    ) {
      if (catA.minCapacityPercent && !catB.minCapacityPercent) {
        return -1;
      }

      if (!catA.minCapacityPercent && catB.minCapacityPercent) {
        return 1;
      }

      const extraAmountA = a.capacity - getResourceMinCapacity(catA, a);
      const extraAmountB = b.capacity - getResourceMinCapacity(catB, b);

      return extraAmountA - extraAmountB;
    }

    if (catA.allowUnderAllocation === catB.allowUnderAllocation) {
      return 0;
    }

    return !catA.allowUnderAllocation ? -1 : 1;
  });

  // We run this block twice to ensure the minimum capacity is allocated first for each resource
  for (let x = 0; x <= 1; x++) {
    for (const res of resources) {
      const category = categoryIndex[res.categoryId]!;
      const minAmount = getResourceMinCapacity(category, res);
      let availableCapacity = x === 0 ? minAmount : res.capacity - minAmount; // In the first iteration, we allocate the minimum. In the second, we allocate the rest

      for (const selTariff of selectedTariffs) {
        const tariffKey = `${selTariff.tariff.id}`;
        const currQuantity = tariffPending[tariffKey]!;

        const amount = Math.min(availableCapacity, currQuantity);
        if (amount === 0) {
          continue;
        }

        availableCapacity -= amount;
        const key = makeKey(res, selTariff.tariff);
        newTariffValues[key] = (newTariffValues[key] ?? 0) + amount;
        tariffPending[tariffKey] = currQuantity - amount;
      }
    }
  }

  return newTariffValues;
};

export const getResourceTariffValuesQuantity = (
  res: SeatMapSlotResource,
  tariff: Tariff,
  tariffValues: ResourceTariffValues,
): number => tariffValues[makeKey(res, tariff)] ?? 0;

const makeKey = (res: SeatMapSlotResource, tariff: Tariff): string => `${res.id}_${tariff.id}`;

export const getTotalCapacity = (resources: SeatMapSlotResource[]): number =>
  resources.reduce((sum, res) => sum + res.capacity, 0);

export const treeshakeUnnecessaryResources = (
  resources: SeatMapSlotResource[],
  tariffSum: number,
): SeatMapSlotResource[] => {
  const totalCapacity = getTotalCapacity(resources);
  let leftover = totalCapacity - tariffSum;

  const minorResources = resources.filter((res) => res.capacity <= leftover);
  minorResources.sort((a, b) => b.capacity - a.capacity);

  const toRemove: { [key: string]: boolean } = {};
  for (const res of minorResources) {
    if (leftover < res.capacity) {
      break;
    }

    leftover -= res.capacity;
    toRemove[res.id] = true;
  }

  return resources.filter((res) => !toRemove[res.id]);
};

export const seatMapResourceList = (seatMap: SeatMapOverview): SeatMapSlotResource[] => {
  const allSlots = seatMap.areas.reduce((carry, area) => [...carry, ...area.slots], [] as SeatMapSlot[]);
  return allSlots.filter((s) => s.type === SeatMapSlotTypeEnum.Resource).map((s) => s.resource!);
};

export const getMovableRemainingQuantity = (
  selectedResources: SeatMapSlotResource[],
  selectedTariffs: SelectedTariff[],
  categoryIndex: { [key: string]: ResourceCategory },
  tariffValues: ResourceTariffValues,
): number => {
  return selectedResources.reduce((sum, res) => {
    const filledAmount = selectedTariffs.reduce(
      (sum, { tariff }) => sum + getResourceTariffValuesQuantity(res, tariff, tariffValues),
      0,
    );
    const minAmount = getResourceMinCapacity(categoryIndex[res.categoryId]!, res);

    return sum + Math.max(filledAmount - minAmount, 0);
  }, 0);
};

export const getMaxAllowedCapacityForResourceList = (
  resourceList: SeatMapSlotResource[],
  remainingQuantityToAllocate: number,
): number | null => {
  return resourceList.reduce(
    (curr: number | null, { capacity: cap }) =>
      cap > remainingQuantityToAllocate && (curr === null || cap < curr) ? cap : curr,
    null,
  );
};

export const getResourceStatus = (
  resource: SeatMapSlotResource,
  selectedResourceIds: string[],
  tariffSum: number,
  categoryIndex: { [key: string]: ResourceCategory },
  maxCapacity: number | null,
  selectedCapacitySum: number,
  remainingQuantityToAllocate: number,
  movableRemainingQuantity: number,
): string => {
  if (selectedResourceIds.includes(resource.id)) {
    return SELECTED_STATUS;
  }

  if (resource.taken) {
    return UNAVAILABLE_STATUS;
  }

  if (selectedCapacitySum >= tariffSum) {
    return UNAVAILABLE_STATUS;
  }

  const category = categoryIndex[resource.categoryId]!;

  if (category.blocked) {
    return UNAVAILABLE_STATUS;
  }

  if (!category.allowUnderAllocation && maxCapacity && resource.capacity > maxCapacity) {
    // TODO adjust
    return UNAVAILABLE_STATUS;
  }

  if (remainingQuantityToAllocate + movableRemainingQuantity < getResourceMinCapacity(category, resource)) {
    return UNAVAILABLE_STATUS;
  }

  return AVAILABLE_STATUS;
};

export interface AllocatedResource {
  resource: SeatMapSlotResource;
  category: ResourceCategory;
  filledAmount: number;
  incomplete: boolean;
  isReady: boolean;
  hasMinValue: boolean;
  resourceValueCents: number;
  shouldShowSubAllocationNotification: boolean;
}

export const getAllocatedResources = (
  selectedResources: SeatMapSlotResource[],
  selectedTariffs: SelectedTariff[],
  categoryIndex: { [key: string]: ResourceCategory },
  tariffValues: ResourceTariffValues,
  remainingQuantityToAllocate: number,
): AllocatedResource[] => {
  return selectedResources.map((resource) => {
    const filledAmount = selectedTariffs.reduce(
      (sum, { tariff }) => sum + getResourceTariffValuesQuantity(resource, tariff, tariffValues),
      0,
    );
    const incomplete = filledAmount < resource.capacity;
    const category = categoryIndex[resource.categoryId]!;
    const isReady = category.allowUnderAllocation || !incomplete;
    const chargeType = category.completeCapacityChargeType;
    const hasMinValue =
      chargeType === ResourceCategoryCompleteCapacityChargeTypeEnum.FixedValue ||
      chargeType === ResourceCategoryCompleteCapacityChargeTypeEnum.TariffPercent;
    const resourceValueCents = getResourceValueCents(resource, category);
    const shouldShowSubAllocationNotification = !!(
      category.allowUnderAllocation &&
      category.minCapacityPercent &&
      remainingQuantityToAllocate > 0 &&
      remainingQuantityToAllocate < getResourceMinCapacity(category, resource)
    );

    return {
      resource,
      category,
      filledAmount,
      incomplete,
      isReady,
      hasMinValue,
      chargeType,
      resourceValueCents,
      shouldShowSubAllocationNotification,
    };
  });
};

export type SeatMapAllocationAttempt = {
  success: boolean;
  missingCapacity: number; // The amount of resource capacity missing to fill the selected tariffs
  missingTariffs: number; // The amount of tariffs missing to fill the available resources
};

/**
 * Upper bound on how many resource combinations the allocation search may expand.
 *
 * The search only returns early when it finds a valid allocation. A selection that cannot be
 * allocated has nothing to return early on, so it walks the whole subset space of the free
 * resources — 2^34 on the seat maps this ran into. Capping the walk turns the pathological case
 * into a best-effort answer instead of a frozen tab. Successful allocations settle in a few
 * hundred expansions, well under this ceiling.
 */
const MAX_ALLOCATION_SEARCH_NODES = 20000;

export const getSeatMapBestAllocationStatus = (
  seatMap: SeatMapOverview,
  selectedTariffs: SelectedTariff[],
): SeatMapAllocationAttempt => {
  const availableResourceList = seatMapResourceList(seatMap).filter((res) => !res.taken);
  const categoryIndex = seatMap.categories.reduce((carry, cat) => ({ ...carry, [cat.id]: cat }), {});
  const totalToFill = selectedTariffs.reduce((sum, { quantity }) => sum + quantity, 0); // Total amount of tariffs to fill

  // When the selection does not fit the free resources there is no allocation to look for, and the
  // shortfall is already known. Searching anyway costs the full subset walk to reach this same
  // answer — the case behind the page freezing once a shopper asks for more seats than are left.
  const availableCapacity = getTotalCapacity(availableResourceList);
  if (totalToFill > availableCapacity) {
    return { success: false, missingCapacity: totalToFill - availableCapacity, missingTariffs: 0 };
  }

  return getBestAllocationForResources([], availableResourceList, selectedTariffs, totalToFill, categoryIndex);
};

const getBestAllocationForResources = (
  selected: SeatMapSlotResource[],
  remaining: SeatMapSlotResource[],
  selectedTariffs: SelectedTariff[],
  totalToFill: number,
  categoryIndex: { [key: string]: ResourceCategory },
  cache: { [key: string]: SeatMapAllocationAttempt } = {},
  budget: { remainingNodes: number } = { remainingNodes: MAX_ALLOCATION_SEARCH_NODES },
): SeatMapAllocationAttempt => {
  selected.sort((a, b) => a.id.localeCompare(b.id));
  const cacheKey = selected.map((res) => res.id).join('-');
  if (cache[cacheKey]) {
    return cache[cacheKey]!;
  }

  const tariffValues = makeResourceTariffValues(selectedTariffs, selected, categoryIndex); // Calculate the amount of each tariff in each resource

  const filledAmount = selected.reduce((carry, res) => {
    return (
      carry +
      selectedTariffs.reduce((sum, { tariff }) => sum + getResourceTariffValuesQuantity(res, tariff, tariffValues), 0)
    );
  }, 0); // Total amount of tariffs filled

  const missingCapacity = totalToFill - filledAmount;
  const missingTariffs = selected.reduce((sum, res) => {
    const minAmount = getResourceMinCapacity(categoryIndex[res.categoryId]!, res);
    const filled = selectedTariffs.reduce(
      (sum, { tariff }) => sum + getResourceTariffValuesQuantity(res, tariff, tariffValues),
      0,
    );

    return sum + Math.max(minAmount - filled, 0);
  }, 0); // Amount missing to fill the minimum capacity of all the selected resources

  let currentAlloc = {
    success: missingCapacity === 0 && missingTariffs === 0,
    missingCapacity,
    missingTariffs,
  };

  if (currentAlloc.success) {
    return currentAlloc;
  }

  for (const seat of remaining) {
    if (budget.remainingNodes <= 0) {
      break;
    }
    budget.remainingNodes -= 1;

    const newSelected = [...selected, seat];
    const minimalResources = treeshakeUnnecessaryResources(newSelected, totalToFill);
    const seatWasIgnored = !minimalResources.find((s) => s.id === seat.id);

    if (seatWasIgnored) {
      continue;
    }

    const remainingSeats = remaining.filter((s) => s.id !== seat.id);
    const newAlloc = getBestAllocationForResources(
      minimalResources,
      remainingSeats,
      selectedTariffs,
      totalToFill,
      categoryIndex,
      cache,
      budget,
    );

    if (newAlloc.success) {
      return newAlloc;
    }

    // If new allocation is better than the current one, update the current allocation
    if (
      (currentAlloc.missingTariffs === 0 && newAlloc.missingTariffs > 0) ||
      (newAlloc.missingTariffs > 0 && newAlloc.missingTariffs < currentAlloc.missingTariffs) ||
      (newAlloc.missingTariffs === currentAlloc.missingTariffs &&
        newAlloc.missingCapacity < currentAlloc.missingCapacity)
    ) {
      currentAlloc = newAlloc;
    }
  }

  cache[cacheKey] = currentAlloc;

  return currentAlloc;
};

export const transformSeatMapForUI = (seatMap: SeatMapOverview): SeatMapOverviewV2 => {
  const areas: SeatMapOverviewV2['areas'] = [];
  const categories: SeatMapOverviewV2['categories'] = [];
  const categoriesMap = new Map<string, SeatMapOverviewV2['categories'][number]>();

  // Pre-transform categories
  for (const category of seatMap.categories) {
    const transformedCategory = {
      id: category.id,
      name: category.name,
      completeCapacityChargeType: category.completeCapacityChargeType as ChargeTypeEnum,
      minCapacityPercent: category.minCapacityPercent,
      allowUnderAllocation: category.allowUnderAllocation,
      color: category.color,
      blocked: category.blocked,
      tariff: category.tariff
        ? {
            id: category.tariff.id,
            typeId: category.tariff.type.id,
            productId: '',
            priceCents: category.tariff.priceCents,
            oldPriceCents: category.tariff.oldPriceCents,
            isDependent: category.tariff.isDependent,
            isDefault: category.tariff.isDefault,
            description: category.tariff.description,
            createdAt: '',
            updatedAt: '',
          }
        : null,
    };
    categories.push(transformedCategory);
    categoriesMap.set(category.id, transformedCategory);
  }

  // Pre-generate ID counter for background images
  let idCounter = Date.now();

  // Transform areas and slots
  for (const area of seatMap.areas) {
    const slots = new Array(area.slots.length);

    for (let i = 0; i < area.slots.length; i++) {
      const slot = area.slots[i]!;
      const resource = slot.resource;

      slots[i] = {
        ...slot,
        type: slot.type as SlotTypeEnum,
        shapeType: slot.shapeType as ShapeTypeEnum,
        rect: slot.rect ?? undefined,
        circle: slot.circle ?? undefined,
        resource: {
          ...resource,
          available: !resource.taken,
          category: categoriesMap.get(resource.categoryId),
        },
      };
    }

    areas.push({
      id: area.id,
      name: area.name ?? '',
      width: area.width,
      height: area.height,
      backgroundImage: area.bgImageUrl
        ? {
            id: (idCounter++).toString(36),
            alternativeText: area.name ?? '',
            styles: [{ style: 'cover', url: area.bgImageUrl }],
          }
        : undefined,
      slots,
    });
  }

  return {
    id: seatMap.id,
    name: '',
    areas,
    categories,
  };
};

/**
 * Returns distinct schedule times from a list of tariffs per type
 */
export const getDistinctScheduleTimes = (tariffsPerType: TariffsPerType[]): ScheduleTime[] => {
  const timesMap: Record<string, ScheduleTime> = {};
  for (const t of tariffsPerType) {
    for (const sch of t.schedulings) {
      for (const st of sch.availableScheduleTimes ?? []) {
        timesMap[st.formattedTime] = st;
      }
    }
  }
  return Object.values(timesMap);
};

/**
 * Creates a hash string from selected tariffs per type for caching purposes
 */
export const createTariffHash = (selectedTariffsPerType: TariffsPerType[]): string => {
  return selectedTariffsPerType
    .filter((t) => t.selectedQuantity > 0)
    .map((t) => `${t.type.id}:${t.selectedQuantity}`)
    .sort()
    .join('|');
};

/**
 * Formats a key for seat map storage/lookup
 */
export const formatSeatMapKey = (schedulingId: string, timeMinutes?: number, selectionsHash = '') =>
  `${schedulingId}${timeMinutes != null ? `-${timeMinutes}` : ''}${selectionsHash ? `-${selectionsHash}` : ''}`;

/**
 * Checks if any tariff has schedulings that require seat selection
 */
export const hasSchedulingRequiringSeatSelection = (tariffsPerType: TariffsPerType[]): boolean =>
  tariffsPerType.some((t) => t.schedulings.some((sch) => sch.requiresSeatSelection));

/**
 * Finds the scheduling that contains a specific time (minutes) and requires seat selection
 */
export const findSchedulingForTime = (
  selectedTariffsPerType: TariffsPerType[],
  minutes: number,
): Scheduling | undefined => {
  for (const tariffPerType of selectedTariffsPerType) {
    for (const sch of tariffPerType.schedulings) {
      if (sch.requiresSeatSelection && schedulingContainsMinute(sch, minutes)) {
        return sch;
      }
    }
  }
  return undefined;
};

/**
 * Checks if there is seat availability for a given time slot
 * Returns true if seats are available, false otherwise
 */
export const hasSeatMapAvailability = (
  minutes: number,
  tariffsPerType: TariffsPerType[],
  validationResults: Record<string, SeatMapAllocationAttempt>,
): boolean => {
  const scheduling = findSchedulingForTime(tariffsPerType, minutes);
  if (!scheduling) return true;

  const result = validationResults[formatSeatMapKey(scheduling.id, minutes)];
  if (!result) return true;

  const hasCapacityButBelowCategoryMinimum = result.missingCapacity === 0 && result.missingTariffs > 0;
  return result.success || hasCapacityButBelowCategoryMinimum;
};
